/* =============================================================================
 * AthleteDocumentsSection — the family's transcripts and schedule flyers
 * (KGH-795 / APS-255)
 * -----------------------------------------------------------------------------
 * Ryan's "Socials and Documents" line asked for "access to player-uploaded
 * documents such as transcripts and schedule flyers". The socials half shipped
 * in 042; the documents half was deferred twice and nothing in the product
 * stored a document until migration 050. This is the family's side of it: the
 * one place a parent adds the file, names it, and decides whether a college
 * coach may open it.
 *
 * SIX THINGS IT IS CAREFUL ABOUT
 *
 *  1. IT NEVER CLAIMS THE FILE IS PUBLIC, BECAUSE IT IS NOT. A document is not
 *     on the unauthenticated share page and migration 050 has no column that
 *     could put it there. The switch on each row means one thing only — a
 *     VERIFIED college coach reading this player's profile can open this file —
 *     and the copy says that rather than borrowing the "share link" language
 *     from ShareSettingsModal, which would be a lie about where the transcript
 *     goes. That distinction matters more for a minor's academic record than it
 *     does for an @handle.
 *
 *  2. IT TELLS THE FAMILY WHICH OF THE THREE STEPS FAILED. The upload is
 *     presign → PUT to S3 → record the row, and the three failures mean
 *     different things to the person holding the phone: nothing left the device,
 *     the bytes did not arrive, or the bytes arrived and the profile does not
 *     know about them. One "upload failed" for all three would leave a parent
 *     guessing whether to look for a half-added transcript. documentsAPI.upload
 *     is one call, so the stage is tracked through its progress callback — see
 *     describeUploadFailure below for why that is sound.
 *
 *  3. IT SHOWS THE PERCENTAGE. A 25 MB scan of a transcript over a gym's wifi
 *     takes long enough that a bar-less button reads as a hang, and the second
 *     tap is what produces a duplicate.
 *
 *  4. IT DOES NOT HAND OUT A DEAD LINK. A document url is a presigned GET good
 *     for fifteen minutes (the list says exactly how long in url_expires_in).
 *     Once a link is past its life the Open control stops being an anchor and
 *     becomes a button that re-lists first, so a family who left the page open
 *     through a game gets the file rather than an S3 403 they cannot act on.
 *
 *  5. IT IS HONEST WHEN THERE IS NOTHING HERE. The empty state teaches: most
 *     parents have never been asked for a schedule flyer and do not know an
 *     unofficial transcript is what coaches expect at this stage.
 *
 *  6. IT DOES THE OPTIMISTIC THING ONLY FOR THE SWITCH. Flipping a boolean can
 *     move under the thumb and roll back if the server disagrees; a rename or a
 *     delete waits for the server, because a row that silently reverts its own
 *     name is worse than a row that took half a second.
 * ========================================================================== */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  AlertCircle,
  Edit2,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Plus,
  Trash2,
  Upload,
  X,
} from 'react-feather';
import {
  documentsAPI,
  validateDocumentFile,
  formatBytes,
  DOCUMENT_KIND_LABELS,
  ACCEPTED_MIME_TYPES,
  MAX_DOCUMENT_BYTES,
} from '../services/documentsAPI';
import type { AthleteDocument, DocumentKind } from '../services/documentsAPI';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { Button, Field, Pill, Select, Skeleton, TextInput, fieldAria } from './ui';

/** The order the picker offers, headline use first. Labels come from the client
 *  so this screen and the coach's screen cannot disagree about a kind. */
const KIND_ORDER: DocumentKind[] = ['transcript', 'schedule_flyer', 'other'];

/** A kind the server has that this build does not must still render as words. */
function kindLabel(kind: DocumentKind): string {
  return DOCUMENT_KIND_LABELS[kind] || DOCUMENT_KIND_LABELS.other;
}

const MAX_MB = Math.round(MAX_DOCUMENT_BYTES / 1048576);

/** Mirrors the length cap the table's CHECK enforces, so the input stops the
 *  family at 120 rather than the server truncating their title silently. */
const TITLE_MAX = 120;

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'var(--aps-action)',
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'underline',
};

/* Only the file input still uses this — the text box and the kind picker are
 * primitives now. No `outline` here: an inline declaration outranks the global
 * `:focus-visible` ring in globals.css, and the file button was drawing nothing
 * on keyboard focus because of it. */
const controlStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontFamily: 'var(--aps-font-sans)',
  fontSize: 'var(--aps-t-body)',
  color: 'var(--aps-ink)',
  background: 'var(--aps-surface)',
  border: '1px solid var(--aps-border-strong)',
  borderRadius: 'var(--aps-r-md)',
};

/** The kind picker, in one place because the add form and every row's rename
 *  form must offer the same three words in the same order. The Select primitive
 *  carries the shared focus, invalid and disabled treatment. */
const KindSelect: React.FC<{
  id: string;
  value: DocumentKind;
  disabled?: boolean;
  describedBy?: string;
  onChange: (kind: DocumentKind) => void;
}> = ({ id, value, disabled, describedBy, onChange }) => (
  <Select
    id={id}
    value={value}
    disabled={disabled}
    aria-describedby={describedBy}
    onChange={(e) => onChange(e.target.value as DocumentKind)}
  >
    {KIND_ORDER.map((k) => (
      <option key={k} value={k}>
        {kindLabel(k)}
      </option>
    ))}
  </Select>
);

function formatDate(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Strip the extension and the separators a phone camera leaves behind, so the
 *  title box arrives pre-filled with something the family recognises. */
function suggestTitle(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, TITLE_MAX);
}

type UploadStage = 'idle' | 'preparing' | 'uploading' | 'recording';

/**
 * Which of the three steps failed, in the words that tell the family what to do
 * next.
 *
 * The stage is enough to tell them apart. `documentsAPI.upload` reports progress
 * only once the presign has come back, and an XHR upload of a File fires a final
 * progress event with loaded === total before `load` resolves — so the stage has
 * reached 'recording' whenever the bytes are actually in the bucket. The error
 * shape is the second signal: anything thrown by the API client carries a status
 * or a code, and anything thrown by the S3 PUT is a bare Error.
 */
function describeUploadFailure(error: unknown, stage: UploadStage): string {
  const err = error as { message?: string; status?: number; code?: string } | null;
  const detail = err?.message ? String(err.message) : '';
  const fromApi = !!(err && (err.status || err.code));

  if (stage === 'preparing' && fromApi) {
    // Step 1. Nothing left the device, so there is nothing half-added anywhere.
    return `${detail || 'We could not start that upload.'} Nothing was sent, so nothing is half-added. Try again.`;
  }
  if (stage === 'recording' || fromApi) {
    // Step 3. The object is in the bucket but no row was written, which means
    // the document is NOT on the profile — saying "try again" is correct and
    // adding it again is safe.
    return `Your file reached us, but we could not add it to the profile — it is not on here yet. ${detail} Add it again.`;
  }
  // Step 2. The bytes never arrived; the record step was never reached.
  return `${detail || 'The upload did not finish.'} Nothing was saved. Check your connection and add the file again.`;
}

interface DocumentRowProps {
  doc: AthleteDocument;
  /** The card's standing note about who can open these. Read to the switch. */
  shareNoteId: string;
  /** Links have aged past their fifteen minutes; Open must re-list first. */
  linksStale: boolean;
  /** Resolves to a fresh, openable url for this document, or throws. */
  onRefreshLink: (doc: AthleteDocument) => Promise<string>;
  /** Optimistic in the parent, which owns the list; throws to roll the row back. */
  onToggleShare: (doc: AthleteDocument, next: boolean) => Promise<void>;
  onSave: (doc: AthleteDocument, changes: { title: string; kind: DocumentKind }) => Promise<void>;
  onDelete: (doc: AthleteDocument) => Promise<boolean>;
}

const DocumentRow: React.FC<DocumentRowProps> = ({
  doc,
  shareNoteId,
  linksStale,
  onRefreshLink,
  onToggleShare,
  onSave,
  onDelete,
}) => {
  const rowId = useId();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(doc.title);
  const [draftKind, setDraftKind] = useState<DocumentKind>(doc.kind);
  const [editError, setEditError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);

  const shared = doc.share_with_coaches !== false;
  const isImage = (doc.content_type || '').startsWith('image/');

  useEffect(() => {
    if (editing) titleRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setDraftTitle(doc.title);
    setDraftKind(doc.kind);
    setEditError(null);
    setRowError(null);
    setEditing(true);
  };

  const submitEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    const cleanTitle = draftTitle.trim();
    if (!cleanTitle) {
      setEditError('Give it a name so you can tell it from the others.');
      return;
    }
    if (busy) return;
    setBusy(true);
    setEditError(null);
    try {
      await onSave(doc, { title: cleanTitle, kind: draftKind });
      setEditing(false);
    } catch (error) {
      setEditError((error as Error)?.message || 'That change did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async () => {
    if (busy) return;
    setBusy(true);
    setRowError(null);
    try {
      await onToggleShare(doc, !shared);
    } catch (error) {
      setRowError(
        `${(error as Error)?.message || 'That change did not save.'} The switch is back where it was.`
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setRowError(null);
    setBusy(true);
    try {
      await onDelete(doc);
    } catch (error) {
      setRowError((error as Error)?.message || 'That did not come off. Try again.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The link on screen is older than the fifteen minutes S3 signed it for, so it
   * is refreshed before it is followed. The blank tab is opened INSIDE the click
   * — a tab opened after an await is a popup and browsers block it — and its
   * opener is severed to get the same protection `rel="noopener noreferrer"`
   * gives the fresh-link anchor below.
   */
  const openStaleLink = async () => {
    if (busy) return;
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setBusy(true);
    setRowError(null);
    try {
      const fresh = await onRefreshLink(doc);
      if (tab) {
        tab.location.href = fresh;
      } else {
        setRowError('Your browser blocked the new tab. The link is refreshed — choose Open again.');
      }
    } catch (error) {
      if (tab) tab.close();
      setRowError((error as Error)?.message || 'That link could not be refreshed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const openControl = (() => {
    if (!doc.url) {
      // The server returns a null url when document storage is unconfigured for
      // the environment. Saying so beats a button that cannot work.
      return (
        <span style={{ fontSize: 13, color: 'var(--aps-text-muted)', fontWeight: 600 }}>
          Cannot be opened right now
        </span>
      );
    }
    if (linksStale) {
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={openStaleLink}
          loading={busy}
          leftIcon={<ExternalLink size={14} aria-hidden="true" />}
          aria-label={`Open ${doc.title} in a new tab`}
        >
          Open
        </Button>
      );
    }
    return (
      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${doc.title} in a new tab`}
        className="aps-btn"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          borderRadius: 'var(--aps-r-md)',
          border: '1.5px solid var(--aps-border-strong)',
          background: 'var(--aps-surface)',
          color: 'var(--aps-ink)',
          fontSize: 13,
          fontWeight: 700,
          textDecoration: 'none',
          lineHeight: 1,
        }}
      >
        <ExternalLink size={14} aria-hidden="true" />
        Open
      </a>
    );
  })();

  return (
    <li style={{ borderTop: '1px solid var(--aps-border)', padding: '14px 0' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 'var(--aps-r-sm)',
            background: 'var(--aps-fill)',
            color: 'var(--aps-text-muted)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {isImage ? <ImageIcon size={18} /> : <FileText size={18} />}
        </span>

        {editing ? (
          /* The editor takes the row's place rather than opening a modal: the
             family is changing two short fields on something they can still see. */
          <form onSubmit={submitEdit} style={{ flex: '1 1 200px', minWidth: 0, display: 'grid', gap: 10 }}>
            <Field
              id={`${rowId}-title`}
              label="Name"
              error={editError}
              accessory={
                <span style={{ fontSize: 12, color: 'var(--aps-text-muted)', fontWeight: 600 }}>
                  {draftTitle.length}/{TITLE_MAX}
                </span>
              }
            >
              <TextInput
                ref={titleRef}
                {...fieldAria(`${rowId}-title`, { error: editError })}
                value={draftTitle}
                maxLength={TITLE_MAX}
                disabled={busy}
                onChange={(e) => setDraftTitle(e.target.value)}
              />
            </Field>
            <Field id={`${rowId}-kind`} label="Kind">
              <KindSelect
                id={`${rowId}-kind`}
                value={draftKind}
                disabled={busy}
                onChange={setDraftKind}
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button type="submit" size="sm" loading={busy}>
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--aps-ink)',
                  overflowWrap: 'anywhere',
                }}
              >
                {doc.title}
              </span>
              <Pill tone="neutral">{kindLabel(doc.kind)}</Pill>
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--aps-text-muted)',
              }}
            >
              {[formatDate(doc.created_at), formatBytes(doc.byte_size)].filter(Boolean).join(' · ')}
            </div>
          </div>
        )}
      </div>

      {!editing && (
        <>
          {/* The switch, its state in words, and the actions. At 375 these stack;
              on a wide card they sit on one line with the actions pushed right. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 10,
              paddingLeft: 48,
            }}
          >
            <button
              type="button"
              role="switch"
              aria-checked={shared}
              aria-label={`Visible to college coaches: ${doc.title}`}
              aria-describedby={shareNoteId}
              disabled={busy}
              onClick={toggleShare}
              style={{
                position: 'relative',
                width: 48,
                height: 28,
                flexShrink: 0,
                padding: 0,
                border: 'none',
                borderRadius: 'var(--aps-r-pill)',
                background: shared ? 'var(--aps-action)' : 'var(--aps-border-strong)',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.55 : 1,
                transition: 'background-color var(--aps-dur-fast) var(--aps-ease-out)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 3,
                  left: 3,
                  width: 22,
                  height: 22,
                  borderRadius: 'var(--aps-r-pill)',
                  background: 'var(--aps-surface)',
                  boxShadow: 'var(--aps-elev-1)',
                  transform: shared ? 'translateX(20px)' : 'none',
                  transition: 'transform var(--aps-dur-fast) var(--aps-ease-out)',
                }}
              />
            </button>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: shared ? 'var(--aps-text-2)' : 'var(--aps-text-muted)',
                flex: '1 1 140px',
                lineHeight: 1.4,
              }}
            >
              {shared ? 'College coaches can open this' : 'Hidden — college coaches cannot open this'}
            </span>

            <span style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {openControl}
              <Button
                variant="ghost"
                size="sm"
                onClick={startEdit}
                disabled={busy}
                leftIcon={<Edit2 size={14} aria-hidden="true" />}
                aria-label={`Rename ${doc.title}`}
              >
                Rename
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={busy}
                leftIcon={<Trash2 size={14} aria-hidden="true" />}
                aria-label={`Remove ${doc.title}`}
                style={{ color: 'var(--aps-danger)' }}
              >
                Remove
              </Button>
            </span>
          </div>

          {rowError && (
            <p
              role="alert"
              style={{
                margin: '8px 0 0',
                paddingLeft: 48,
                fontSize: 13,
                color: 'var(--aps-text-2)',
                lineHeight: 1.5,
              }}
            >
              {rowError}
            </p>
          )}
        </>
      )}
    </li>
  );
};

interface AthleteDocumentsSectionProps {
  athleteId: string;
  /**
   * Used in the empty state's copy, so the sentence about a coach needing the
   * flyer to know where to stand names the player rather than saying "this
   * athlete" at a parent who is looking at their own daughter's profile.
   */
  athleteFirstName?: string;
}

const AthleteDocumentsSection: React.FC<AthleteDocumentsSectionProps> = ({
  athleteId,
  athleteFirstName,
}) => {
  const { sessionToken } = useAuth();
  const formId = useId();
  const shareNoteId = `${formId}-share-note`;

  const [documents, setDocuments] = useState<AthleteDocument[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // How long a presigned GET is good for, straight from the list rather than a
  // number this file guesses at, and when the ones on screen were minted.
  const [linkTtl, setLinkTtl] = useState(900);
  const [linksFetchedAt, setLinksFetchedAt] = useState<number | null>(null);
  const [linksStale, setLinksStale] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<DocumentKind>('transcript');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stage, setStage] = useState<UploadStage>('idle');
  const [progress, setProgress] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // A second submit resolves before React has re-rendered the disabled button,
  // and the cost of losing that race is a duplicate transcript on the profile.
  const uploadInFlight = useRef(false);
  const stageRef = useRef<UploadStage>('idle');

  const [pendingDelete, setPendingDelete] = useState<AthleteDocument | null>(null);
  const { ConfirmComponent: RemoveConfirm, confirm: confirmRemove } = useConfirm({
    title: 'Remove this document?',
    message: pendingDelete
      ? `"${pendingDelete.title}" comes off this profile. Any college coach who could open it no longer can. You can add it again later.`
      : '',
    confirmText: 'Remove',
    cancelText: 'Keep it',
    variant: 'danger',
  });

  const busy = stage !== 'idle';

  /* Which athlete this card is currently showing. A guardian switching athletes
   * leaves the first list in flight, and without this the slower response lands
   * on the faster one - one sister's transcripts under the other's name. */
  const shownAthlete = useRef(athleteId);

  /**
   * Lists, which is also what refreshes every row's link and restarts the
   * fifteen-minute clock. Returns the rows so a caller that needs one url in
   * particular does not have to wait for a re-render to read it.
   */
  const load = useCallback(async (): Promise<AthleteDocument[]> => {
    if (!sessionToken || !athleteId) return [];
    const response = await documentsAPI.list(sessionToken, athleteId);
    const rows = response.documents || [];
    if (shownAthlete.current !== athleteId) return rows;
    setDocuments(rows);
    setLinkTtl(response.url_expires_in || 900);
    setLinksFetchedAt(Date.now());
    setLinksStale(false);
    return rows;
  }, [sessionToken, athleteId]);

  /** The same load, with the screen's loading and error states around it. */
  const loadForDisplay = useCallback(async () => {
    setDocuments(null);
    setLoadError(null);
    try {
      await load();
    } catch (error) {
      if (shownAthlete.current !== athleteId) return;
      setLoadError(
        (error as Error)?.message || 'We could not load these documents. Check your connection.'
      );
    }
  }, [load, athleteId]);

  useEffect(() => {
    if (!sessionToken || !athleteId) return;
    shownAthlete.current = athleteId;
    loadForDisplay();
  }, [sessionToken, athleteId, loadForDisplay]);

  /* The links on screen go stale on a timer rather than being silently refetched
   * every quarter hour: a profile left open in a tab should not keep calling the
   * API, and the family only needs a fresh link at the moment they open one. A
   * minute of headroom, because a link that expires while the PDF viewer is
   * still loading is a 403 with nothing to click. */
  useEffect(() => {
    if (!linksFetchedAt) return undefined;
    const dueIn = Math.max(linkTtl - 60, 0) * 1000 - (Date.now() - linksFetchedAt);
    if (dueIn <= 0) {
      setLinksStale(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setLinksStale(true), dueIn);
    return () => window.clearTimeout(timer);
  }, [linksFetchedAt, linkTtl]);

  const handlePickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files && event.target.files[0];
    setNotice(null);
    if (!picked) {
      setFile(null);
      setUploadError(null);
      return;
    }
    setFile(picked);
    // Before the network, with the same rules the server enforces, so a 40 MB
    // scan is refused now rather than after four minutes of uploading.
    setUploadError(validateDocumentFile(picked));
    setTitle((current) => (current.trim() ? current : suggestTitle(picked.name)));
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sessionToken || !athleteId) return;
    if (uploadInFlight.current) return;
    if (!file) {
      setUploadError('Choose a file first.');
      return;
    }
    const problem = validateDocumentFile(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      setUploadError('Give it a name so you can tell it from the others.');
      return;
    }

    uploadInFlight.current = true;
    stageRef.current = 'preparing';
    setStage('preparing');
    setProgress(0);
    setUploadError(null);
    setNotice(null);
    try {
      const created = await documentsAPI.upload(
        sessionToken,
        athleteId,
        file,
        { title: cleanTitle, kind },
        (percent) => {
          stageRef.current = percent >= 100 ? 'recording' : 'uploading';
          setStage(stageRef.current);
          setProgress(percent);
        }
      );
      // Newest first, matching the list's own ORDER BY, and the row carries the
      // link the record step just minted. linksFetchedAt is deliberately NOT
      // restarted: the older rows' links are still as old as they were, and
      // treating them as stale early only costs a re-list.
      setDocuments((prev) => [created, ...(prev || [])]);
      setFile(null);
      setTitle('');
      setKind('transcript');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setNotice(`${created.title} added. College coaches can open it unless you switch it off.`);
    } catch (error) {
      setUploadError(describeUploadFailure(error, stageRef.current));
    } finally {
      uploadInFlight.current = false;
      stageRef.current = 'idle';
      setStage('idle');
      setProgress(0);
    }
  };

  const handleToggleShare = useCallback(
    async (doc: AthleteDocument, next: boolean) => {
      if (!sessionToken) throw new Error('Sign in again to change this.');
      const before = doc.share_with_coaches !== false;
      setDocuments((prev) =>
        prev ? prev.map((d) => (d.id === doc.id ? { ...d, share_with_coaches: next } : d)) : prev
      );
      try {
        await documentsAPI.update(sessionToken, doc.id, { share_with_coaches: next });
      } catch (error) {
        setDocuments((prev) =>
          prev ? prev.map((d) => (d.id === doc.id ? { ...d, share_with_coaches: before } : d)) : prev
        );
        throw error;
      }
    },
    [sessionToken]
  );

  const handleSave = useCallback(
    async (doc: AthleteDocument, changes: { title: string; kind: DocumentKind }) => {
      if (!sessionToken) throw new Error('Sign in again to change this.');
      const response = await documentsAPI.update(sessionToken, doc.id, changes);
      setDocuments((prev) =>
        prev
          ? prev.map((d) =>
              // The PATCH response carries no url — it is not a read path — so the
              // row keeps the link it already has. Spreading the response blindly
              // would blank the Open action the moment somebody renamed something.
              d.id === doc.id ? { ...d, ...response.document, url: d.url } : d
            )
          : prev
      );
    },
    [sessionToken]
  );

  const handleDelete = useCallback(
    async (doc: AthleteDocument) => {
      setPendingDelete(doc);
      const confirmed = await confirmRemove();
      setPendingDelete(null);
      if (!confirmed) return false;
      if (!sessionToken) throw new Error('Sign in again to change this.');
      await documentsAPI.remove(sessionToken, doc.id);
      setDocuments((prev) => (prev ? prev.filter((d) => d.id !== doc.id) : prev));
      setNotice(`${doc.title} removed.`);
      return true;
    },
    [confirmRemove, sessionToken]
  );

  /** Re-lists and hands back this document's newly signed url. */
  const handleRefreshLink = useCallback(
    async (doc: AthleteDocument) => {
      const rows = await load();
      const fresh = rows.find((d) => d.id === doc.id);
      if (!fresh || !fresh.url) {
        throw new Error('That link could not be refreshed. Reload the page and try again.');
      }
      return fresh.url;
    },
    [load]
  );

  const openForm = () => {
    setFormOpen(true);
    setNotice(null);
    // The file is the first decision, so that is where the keyboard lands.
    window.setTimeout(() => fileInputRef.current?.focus(), 0);
  };

  const count = documents ? documents.length : 0;
  const whose = athleteFirstName ? `${athleteFirstName}'s` : 'these';

  const addButton = (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => (formOpen ? setFormOpen(false) : openForm())}
      aria-expanded={formOpen}
      aria-controls={`${formId}-form`}
      leftIcon={formOpen ? <X size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
    >
      {formOpen ? 'Cancel' : 'Add a document'}
    </Button>
  );

  return (
    <section
      aria-labelledby={`${formId}-heading`}
      style={{
        background: 'var(--aps-surface)',
        borderRadius: 12,
        border: '1px solid var(--aps-border)',
        padding: 20,
      }}
    >
      <RemoveConfirm />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <FileText
            size={20}
            style={{ color: 'var(--aps-action)', flexShrink: 0, marginTop: 2 }}
            aria-hidden="true"
          />
          <h3 id={`${formId}-heading`} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--aps-ink)' }}>
            Documents{count > 0 ? ` (${count})` : ''}
          </h3>
        </div>
        {addButton}
      </div>

      {/* Said once, at card level, and pointed at by every switch below. It is
          the sentence that keeps a family from believing a transcript lands on
          their public share link, which it never does. */}
      <p
        id={shareNoteId}
        style={{ margin: '10px 0 0', fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.55 }}
      >
        A verified college coach reading this profile can open the documents you leave switched on.
        They are never on your public share link, and nobody else on the internet can reach them.
      </p>

      {formOpen && (
        <form
          id={`${formId}-form`}
          onSubmit={handleUpload}
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 'var(--aps-r-md)',
            background: 'var(--aps-surface-2)',
            border: '1px solid var(--aps-border)',
            display: 'grid',
            gap: 12,
          }}
        >
          <Field
            id={`${formId}-file`}
            label="File"
            required
            hint={`A PDF or a photo (JPEG, PNG, HEIC or WebP), up to ${MAX_MB} MB. A photo of a printed transcript is fine.`}
            error={uploadError}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIME_TYPES.join(',')}
              disabled={busy}
              onChange={handlePickFile}
              {...fieldAria(`${formId}-file`, { hint: true, error: uploadError })}
              style={{
                ...controlStyle,
                padding: '10px 12px',
                fontSize: 'var(--aps-t-label)',
                cursor: busy ? 'not-allowed' : 'pointer',
                borderColor: uploadError ? 'var(--aps-danger)' : 'var(--aps-border-strong)',
              }}
            />
          </Field>

          <Field
            id={`${formId}-title`}
            label="Name it"
            required
            accessory={
              <span style={{ fontSize: 12, color: 'var(--aps-text-muted)', fontWeight: 600 }}>
                {title.length}/{TITLE_MAX}
              </span>
            }
          >
            <TextInput
              {...fieldAria(`${formId}-title`)}
              value={title}
              maxLength={TITLE_MAX}
              disabled={busy}
              placeholder="Fall semester transcript"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>

          <Field id={`${formId}-kind`} label="What is it">
            <KindSelect id={`${formId}-kind`} value={kind} disabled={busy} onChange={setKind} />
          </Field>

          {busy && (
            /* The percentage, because a 25 MB file on a phone with no bar looks
               like a hang and the second tap is what makes a duplicate. */
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--aps-text-2)',
                  marginBottom: 6,
                }}
              >
                <span>
                  {stage === 'preparing'
                    ? 'Getting ready…'
                    : stage === 'uploading'
                    ? 'Uploading…'
                    : 'Adding it to the profile…'}
                </span>
                {stage !== 'preparing' && <span>{progress}%</span>}
              </div>
              <div
                role="progressbar"
                aria-label={`Uploading ${file ? file.name : 'your document'}`}
                aria-valuemin={0}
                aria-valuemax={100}
                {...(stage === 'preparing' ? {} : { 'aria-valuenow': progress })}
                style={{
                  height: 8,
                  borderRadius: 'var(--aps-r-pill)',
                  background: 'var(--aps-fill)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${stage === 'preparing' ? 4 : progress}%`,
                    height: '100%',
                    background: 'var(--aps-action)',
                    borderRadius: 'var(--aps-r-pill)',
                    transition: 'width var(--aps-dur-base) var(--aps-ease-out)',
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              loading={busy}
              disabled={!file || !!uploadError}
              leftIcon={<Upload size={15} aria-hidden="true" />}
            >
              {busy ? 'Adding…' : 'Add it'}
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => setFormOpen(false)}>
              Done
            </Button>
          </div>
        </form>
      )}

      {/* Everything this card announces after the fact — a file added, a file
          removed — so a screen reader hears it without moving focus. */}
      <p aria-live="polite" style={{ margin: notice ? '12px 0 0' : 0, fontSize: 13.5, color: 'var(--aps-success-text)', fontWeight: 600 }}>
        {notice}
      </p>

      {documents === null && !loadError ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rect" height={64} radiusPx={8} aria-label={`Loading ${whose} documents`} />
          ))}
        </div>
      ) : loadError ? (
        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle
            size={16}
            style={{ color: 'var(--aps-text-subtle)', flexShrink: 0, marginTop: 2 }}
            aria-hidden="true"
          />
          <p style={{ margin: 0, fontSize: 14, color: 'var(--aps-text-2)', lineHeight: 1.5 }} role="alert">
            {loadError}{' '}
            <button type="button" onClick={loadForDisplay} style={linkButtonStyle}>
              Try again
            </button>
          </p>
        </div>
      ) : count === 0 ? (
        /* Not the shared EmptyState: that primitive is one centred line under an
           icon, and this copy has a job to do. Most parents have never been asked
           for either of these files and do not know what a coach expects, so the
           empty state explains both and then offers the button. */
        <div
          style={{
            marginTop: 14,
            padding: 16,
            borderRadius: 'var(--aps-r-md)',
            background: 'var(--aps-fill)',
          }}
        >
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--aps-ink)' }}>
            Nothing added yet
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--aps-ink)' }}>A transcript</strong> is the record of
            grades and courses behind the GPA on this profile. An unofficial copy printed or saved
            from your school's parent portal is what college coaches expect at this stage — it does
            not have to be the sealed official one.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--aps-ink)' }}>A schedule flyer</strong> is the one-page
            sheet a tournament hands out: dates, field numbers, game times. A coach who wants to
            watch {athleteFirstName || 'this player'} needs it to know where to stand and when.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--aps-text-2)', lineHeight: 1.6 }}>
            Whatever you add, a verified college coach reading this profile can open it, and you can
            switch any single one off later.
          </p>
          {!formOpen && (
            <div style={{ marginTop: 12 }}>
              <Button onClick={openForm} leftIcon={<Plus size={15} aria-hidden="true" />}>
                Add a document
              </Button>
            </div>
          )}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
          {(documents || []).map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              shareNoteId={shareNoteId}
              linksStale={linksStale}
              onRefreshLink={handleRefreshLink}
              onToggleShare={handleToggleShare}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export default AthleteDocumentsSection;
